import { Zap, Globe, ShoppingCart, TrendingUp, Shield, CheckCircle, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function PublicLanding() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="border-b border-zinc-800">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-xl flex items-center justify-center">
              <Zap className="w-5 h-5 text-black" />
            </div>
            <span className="text-xl font-bold">CAUCHERA</span>
          </div>
          <button
            onClick={() => navigate('/login')}
            className="px-4 py-2 bg-zinc-800 text-white rounded-lg hover:bg-zinc-700 transition-colors text-sm"
          >
            Acceso CEO
          </button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full text-emerald-400 text-sm mb-6">
            <Globe className="w-4 h-4" />
            Global E-Commerce Platform
          </div>
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
            Automated
            <span className="text-emerald-400"> E-Commerce </span>
            Solutions
          </h1>
          
          <p className="text-lg text-zinc-400 mb-8 max-w-2xl mx-auto">
            CAUCHERA is an AI-powered e-commerce platform that automates product research, 
            store creation, and order fulfillment for global dropshipping operations.
          </p>

          <div className="flex flex-wrap justify-center gap-4">
            <div className="flex items-center gap-2 text-zinc-300">
              <CheckCircle className="w-5 h-5 text-emerald-400" />
              <span>Automated Fulfillment</span>
            </div>
            <div className="flex items-center gap-2 text-zinc-300">
              <CheckCircle className="w-5 h-5 text-emerald-400" />
              <span>Global Shipping</span>
            </div>
            <div className="flex items-center gap-2 text-zinc-300">
              <CheckCircle className="w-5 h-5 text-emerald-400" />
              <span>Secure Payments</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 px-4 border-t border-zinc-800">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl font-bold text-center mb-12">Our Services</h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6">
              <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center mb-4">
                <ShoppingCart className="w-6 h-6 text-blue-400" />
              </div>
              <h3 className="text-lg font-semibold mb-2">E-Commerce Stores</h3>
              <p className="text-zinc-400 text-sm">
                We create and manage high-converting online stores for consumer products, 
                handling everything from product sourcing to customer delivery.
              </p>
            </div>

            <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6">
              <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center mb-4">
                <TrendingUp className="w-6 h-6 text-emerald-400" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Product Research</h3>
              <p className="text-zinc-400 text-sm">
                AI-powered analysis of market trends, demand patterns, and profit 
                opportunities across multiple data sources worldwide.
              </p>
            </div>

            <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6">
              <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center mb-4">
                <Globe className="w-6 h-6 text-purple-400" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Global Fulfillment</h3>
              <p className="text-zinc-400 text-sm">
                Automated order processing and worldwide shipping through our 
                network of verified suppliers and logistics partners.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-16 px-4 border-t border-zinc-800">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 mb-6">
            <Shield className="w-6 h-6 text-emerald-400" />
            <span className="text-lg font-semibold">Secure & Compliant</span>
          </div>
          
          <p className="text-zinc-400 mb-8">
            We use industry-standard security measures including SSL encryption, 
            PCI-compliant payment processing through Stripe, and secure data handling practices.
          </p>

          <div className="flex flex-wrap justify-center gap-6">
            <div className="flex items-center gap-2 px-4 py-2 bg-zinc-900 rounded-lg">
              <img src="https://cdn.brandfolder.io/KGT2DTA4/at/8vbr8k4mr5xjwk4hxq4t9vs/Stripe_wordmark_-_blurple.svg" alt="Stripe" className="h-6" />
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-zinc-900 rounded-lg text-zinc-300">
              <Shield className="w-5 h-5" />
              <span className="text-sm">SSL Secured</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-zinc-900 rounded-lg text-zinc-300">
              <CheckCircle className="w-5 h-5" />
              <span className="text-sm">PCI Compliant</span>
            </div>
          </div>
        </div>
      </section>

      {/* Business Info */}
      <section className="py-16 px-4 border-t border-zinc-800 bg-zinc-900/50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-center mb-8">Business Information</h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-lg font-semibold mb-4 text-emerald-400">Company Details</h3>
              <ul className="space-y-2 text-zinc-400 text-sm">
                <li><strong className="text-zinc-300">Business Name:</strong> CAUCHERA LLC</li>
                <li><strong className="text-zinc-300">Business Type:</strong> E-Commerce / Online Retail</li>
                <li><strong className="text-zinc-300">Incorporated:</strong> Delaware, USA</li>
                <li><strong className="text-zinc-300">Industry:</strong> Dropshipping & Digital Commerce</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-4 text-emerald-400">What We Sell</h3>
              <ul className="space-y-2 text-zinc-400 text-sm">
                <li>• Consumer electronics and accessories</li>
                <li>• Home and lifestyle products</li>
                <li>• Health and wellness items</li>
                <li>• Fashion and accessories</li>
                <li>• Trending viral products</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-zinc-800">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-lg flex items-center justify-center">
              <Zap className="w-4 h-4 text-black" />
            </div>
            <span className="font-semibold">CAUCHERA LLC</span>
          </div>
          
          <div className="text-zinc-500 text-sm">
            © 2025 CAUCHERA LLC. All rights reserved.
          </div>
          
          <div className="flex gap-4 text-zinc-400 text-sm">
            <a href="mailto:contact@tirachina.com" className="hover:text-white transition-colors">
              contact@tirachina.com
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
